import { faHeadset, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateTime } from '@supply-mover-2-0/core';
import {
	IssueStatus,
	PORTAL_ISSUE_MESSAGE_MAX_LENGTH,
	type PortalIssueMessageDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { EmptyState } from '../components/EmptyState';
import { IssueStatusChip } from '../components/IssueStatusChip';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { HistoryListSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalIssueService from '../services/portalIssueService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * One message bubble of the support thread (PD-13773). Customer messages
 * sit right-aligned in the accent color; support replies left-aligned on
 * the surface color — the same visual language as a chat.
 */
function ThreadMessage({
	message,
}: {
	message: PortalIssueMessageDto;
}): JSX.Element {
	const { t } = useTranslation();
	const isCustomer = message.author === 'customer';

	return (
		<div className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}>
			<div
				className={`max-w-[85%] md:max-w-[70%] rounded-xl px-3 py-2 ${
					isCustomer
						? 'bg-portal-accent/10 border border-portal-accent/20'
						: 'bg-brand-surface border border-brand-border'
				}`}
			>
				<div className="text-xs text-brand-muted mb-0.5">
					{isCustomer
						? t('You')
						: message.authorName != null
							? t(message.authorName)
							: t('Support team')}
					{' · '}
					{formatDateTime(message.createdDate as unknown as string)}
				</div>
				<div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
					{t(message.body)}
				</div>
			</div>
		</div>
	);
}

/**
 * Support request detail + communication thread (PD-13773). Shows the
 * customer's original submission, every portal-visible reply from the
 * support team, and a reply box. Replying to a Resolved request reopens
 * it so the support team sees the new correspondence.
 */
export function SupportIssueDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const params = useParams<{ id: string }>();
	const issueId = params.id != null ? parseInt(params.id, 10) : null;
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const queryClient = useQueryClient();

	const [reply, setReply] = useState('');

	const { data, isLoading, isError } = useQuery({
		queryKey: ['portal-issue', issueId],
		queryFn: () => portalIssueService.getPortalIssue(token ?? '', issueId ?? 0),
		enabled: token != null && issueId != null,
		staleTime: 15_000,
	});

	const replyMutation = useMutation({
		mutationFn: (message: string) =>
			portalIssueService.replyToPortalIssue(token ?? '', issueId ?? 0, {
				message,
			}),
		onSuccess: (response) => {
			// The reply response carries the refreshed thread — swap it in and
			// invalidate the list so its message counts / status stay current.
			queryClient.setQueryData(['portal-issue', issueId], response.detail);
			void queryClient.invalidateQueries({ queryKey: ['portal-issues'] });
			setReply('');
		},
	});

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	const trimmedReply = reply.trim();
	const canSend =
		trimmedReply.length > 0 &&
		trimmedReply.length <= PORTAL_ISSUE_MESSAGE_MAX_LENGTH &&
		!replyMutation.isPending;

	const handleSend = (): void => {
		if (canSend) {
			replyMutation.mutate(trimmedReply);
		}
	};

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Support" backPath="/support" />
			<AccountBreadcrumb current="Support" />

			{isLoading ? (
				<HistoryListSkeleton />
			) : isError || data == null ? (
				<EmptyState
					icon={faHeadset}
					title="Support request not found"
					hint="It may have been removed, or the link is incorrect."
				/>
			) : (
				<>
					{/* Header: subject, reference, category, status */}
					<div className="mb-4">
						<div className="flex items-start justify-between gap-3">
							<h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
								{t(data.name)}
							</h1>
							<IssueStatusChip status={data.status} />
						</div>
						<div className="text-xs text-brand-muted mt-1">
							{t('#')}
							{data.id}
							{data.type != null && (
								<>
									{' · '}
									{t(data.type)}
								</>
							)}
							{' · '}
							{t('Submitted')}{' '}
							{formatDateTime(data.createdDate as unknown as string)}
						</div>
					</div>

					{/* Communication thread */}
					<div className="flex flex-col gap-3 mb-4">
						{data.thread.map((message) => (
							<ThreadMessage key={message.id} message={message} />
						))}
						{data.thread.length === 0 && (
							<p className="text-sm text-brand-muted">
								{t('No messages yet.')}
							</p>
						)}
					</div>

					{/* Reply box */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
						{(data.status as IssueStatus) === IssueStatus.DONE && (
							<p className="text-xs text-brand-muted mb-2">
								{t(
									'This request is resolved. Replying will reopen it for the support team.',
								)}
							</p>
						)}
						<textarea
							value={reply}
							onChange={(e) => {
								setReply(e.target.value);
							}}
							maxLength={PORTAL_ISSUE_MESSAGE_MAX_LENGTH}
							placeholder={t('Write a reply...')}
							disabled={replyMutation.isPending}
							rows={3}
							className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100 resize-none"
						/>
						{replyMutation.isError && (
							<p className="text-xs text-red-600 dark:text-red-400 mt-1">
								{t("We couldn't send your reply right now. Please try again.")}
							</p>
						)}
						<div className="flex justify-end mt-2">
							<button
								type="button"
								onClick={handleSend}
								disabled={!canSend}
								className="px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50 flex items-center gap-2 min-h-touch"
							>
								<FontAwesomeIcon icon={faPaperPlane} />
								{replyMutation.isPending ? t('Sending...') : t('Send Reply')}
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
